/**
 * Shell banner ordering + capping (DPA clause 8.3 delivery). Pure and React-free
 * so it can be attacked directly instead of through a source-text regex —
 * `banners.reverse()` defeated the prior regex pin before this slice, keeping
 * both patterns matching while dropping privacy (CLAUDE.md rule 5: the rule
 * now lives in one function every caller goes through).
 *
 * `privacy` is a separate parameter, never a member of `rest` — it is added
 * back onto `shown` unconditionally, so no reordering or truncation of `rest`
 * can make it the suppressed one.
 */

const MAX_SHOWN = 3;

export interface ShellBannerDescriptor<T> {
  readonly key: string;
  readonly element: T;
  readonly label: string;
  readonly href: string;
}

export interface CappedShellBanners<T> {
  readonly shown: readonly ShellBannerDescriptor<T>[];
  readonly suppressed: readonly ShellBannerDescriptor<T>[];
}

export function capShellBanners<T>(
  privacy: ShellBannerDescriptor<T> | null,
  rest: readonly ShellBannerDescriptor<T>[],
): CappedShellBanners<T> {
  const privacySlot = privacy === null ? [] : [privacy];
  const budget = Math.max(0, MAX_SHOWN - privacySlot.length);
  return {
    shown: [...privacySlot, ...rest.slice(0, budget)],
    suppressed: rest.slice(budget),
  };
}
